import {
  Button,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
  Spin,
  Typography,
  Radio,
  Collapse,
  TreeSelect,
} from "antd";
import MultiSelectWithTags from "components/field-filter/DietaryRestrictionsSelect";
import TreeSelectWithTags from "components/tree-select-with-tags/TreeSelectWithTags";
import { transformToTreeSelectData } from "helpers/categories-helper";
import { useEffect, useState, useCallback } from "react";
import { getCreatorsCategories } from "../../services/SauceService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBills,
  faUsers,
  faUtensils,
  faSearch,
  faMoneyBill,
  faMobileAlt,
  faFilter,
  faTags,
  faVenusMars,
  faTriangleExclamation,
  faStar,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import InfluencerCompBox from "components/influencer-comp/InfluencerComp";
import { formatNumberKMBT } from "pages/influencers/InfluencerCard";
import { formatUSD } from "helpers/money-format";
import {
  CrownFilled,
  InstagramOutlined,
  TikTokOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { BusinessState } from "redux-context/business";
import CompTierCard from "./CompTierCard";

const dietaryRestrictions = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "lactoseIntolerance", label: "Lactose Intolerance" },
  { value: "glutenIntolerance", label: "Gluten Intolerance" },
  { value: "noPork", label: "No Pork" },
  { value: "nutsAllergy", label: "Nuts Allergies" },
  { value: "pescatarian", label: "Pescatarian" },
  { value: "shellfishAllergy", label: "Shellfish Allergy" },
];

interface OfferStepProps {
  form: any;
  loading: boolean;
  locationAvailability: any;
  showSocialMediaName?: boolean;
  handleValueChange: () => void;
  handleValueChangeDebounced: () => void;
  onClickNext: () => void;
  onClickBack?: () => void;
  onCompensationGiftedSelected?: () => void;
  onCompensationPaidSelected?: () => void;
  onPlatformInstagramSelected?: () => void;
  onPlatformTiktokSelected?: () => void;
  onOfferContinueClicked?: () => void;
  textNext?: string;
  textBack?: string;
}

const OfferStep: React.FC<OfferStepProps> = ({
  form,
  loading,
  locationAvailability,
  showSocialMediaName = true,
  handleValueChange,
  handleValueChangeDebounced,
  onClickNext,
  onClickBack,
  onCompensationGiftedSelected,
  onCompensationPaidSelected,
  onPlatformInstagramSelected,
  onPlatformTiktokSelected,
  onOfferContinueClicked,
  textNext = "Continue",
  textBack = "Back",
}) => {
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const result: any = await getCreatorsCategories();
      const treeData = transformToTreeSelectData(result);
      setCategoryOptions(treeData);
    } catch (err) {
      setCategoryOptions([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const [customTiers, setCustomTiers] = useState<any[]>([]);
  const [expandedTiers, setExpandedTiers] = useState<Record<string, boolean>>({
    food: false,
    medium: false,
    large: false,
  });

  const getTierFromID = (refId: string, tiers: any[]) => {
    return tiers.find((t: any) => t._id === refId) || {};
  };

  // Initialize `tiers` array in the form if not present
  useEffect(() => {
    const existing = form.getFieldValue("tiers");
    if (!existing) {
    } else {
      // populate customTiers expand state from existing tiers (those not food|medium|large)
      const customs = (existing || [])
        .filter((t: any) => t._id && t._id.toString().startsWith("custom"))
        .map((t: any) => ({
          _id: t._id,
          name: t.compName || "Custom",
          expanded: true,
        }));
      setCustomTiers(customs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // const toggleTierExpand = (id: string) => {
  //   setCustomTiers((s) =>
  //     s.map((t) => (t.id === id ? { ...t, expanded: !t.expanded } : t))
  //   );
  // };

  const toggleExpandedTier = (id: string) => {
    setExpandedTiers((s) => ({ ...s, [id]: !s[id] }));
  };

  // const tierFoodComp = Form.useWatch(["compensationTier", "food"], form);
  // const tierMedium = Form.useWatch(["compensationTier", "medium"], form);
  // const tierLarge = Form.useWatch(["compensationTier", "large"], form);
  const minFollowersAmount = Form.useWatch("minFollowersAmount", form);
  const foodComp = Form.useWatch("foodComp", form);

  const { subscription } = useSelector(
    (state: BusinessState) => state.business
  );

  return (
    <>
      <Row gutter={32} className="campaign-offer-panel">
        <Col className="campaign-offer-left">
          <Collapse
            defaultActiveKey={["compensation"]}
            style={{ marginBottom: 32 }}
            className="campaign-offer-tiers"
            bordered={false}
          >
            <Collapse.Panel
              header={
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: 20,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FontAwesomeIcon icon={faLayerGroup} color="#ff9f1c" />
                  Compensation
                </div>
              }
              key="compensation"
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                <Form.List name={"tiers"}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field) => {
                        const tiers = form.getFieldValue("tiers") || [];
                        const tier = tiers[field.name] || {};
                        const isDefault = ["food", "medium", "large"].includes(
                          tier._id
                        );
                        const expanded = isDefault
                          ? !!expandedTiers[tier._id]
                          : !!customTiers.find((c) => c._id === tier._id)
                              ?.expanded;

                        return (
                          <CompTierCard
                            key={tier._id || field.key}
                            id={tier._id || `tier_${field.key}`}
                            color={
                              tier._id === "food"
                                ? "#06C167"
                                : tier._id === "medium"
                                ? "#ff9f1c"
                                : tier._id === "large"
                                ? "#1F3FFF"
                                : "#000"
                            }
                            field={field}
                            loading={loading}
                            defaultTier={isDefault}
                            expanded={expanded}
                            name="tiers"
                            disableCashComp={tier._id === "food"}
                            onToggle={() => {
                              if (isDefault) toggleExpandedTier(tier._id);
                              else {
                                setCustomTiers((s) =>
                                  s.map((c) =>
                                    c._id === tier._id
                                      ? { ...c, expanded: !c.expanded }
                                      : c
                                  )
                                );
                              }
                            }}
                            onRemove={() => {
                              const idToRemove = tier._id;
                              remove(field.name);
                              if (idToRemove)
                                setCustomTiers((s) =>
                                  s.filter((c) => c._id !== idToRemove)
                                );
                              handleValueChange();
                            }}
                            onApply={() => handleValueChange()}
                            onEnabledChange={(enabled) => {
                              if (!enabled) {
                                return;
                              }
                              if (tier._id === "food") {
                                onCompensationGiftedSelected?.();
                              }
                              if (tier._id === "medium") {
                                onCompensationPaidSelected?.();
                              }
                            }}
                          />
                        );
                      })}

                      <div>
                        <button
                          type="button"
                          onClick={() => {
                            const id = `custom_${Date.now()}`;
                            const newTier = {
                              id,
                              _id: id,
                              name: "Custom Comp",
                              enabled: true,
                              guestAllowed: true,
                              minFollowers: undefined,
                              maxFollowers: undefined,
                              minEngagement: undefined,
                              barterComp: undefined,
                              minCash: undefined,
                              maxCash: undefined,
                            };
                            add(newTier);

                            setCustomTiers((s) => [
                              ...s,
                              { _id: id, name: newTier.name, expanded: true },
                            ]);
                            // handleValueChange();
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#ff9f1c",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          Add Custom Comp
                        </button>
                      </div>
                    </>
                  )}
                </Form.List>
              </div>
            </Collapse.Panel>
          </Collapse>

          {showSocialMediaName && (
            <Collapse
              defaultActiveKey={["2"]}
              style={{ marginBottom: 32 }}
              className="campaign-offer-platform"
              bordered={false}
            >
              <Collapse.Panel
                header={
                  <div
                    style={{
                      fontWeight: 400,
                      fontSize: 18,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <FontAwesomeIcon icon={faMobileAlt} color="#ff9f1c" />{" "}
                    Select Platform
                  </div>
                }
                key="2"
              >
                <Form.Item name="socialMediaName" noStyle>
                  <Radio.Group
                    className="platform-options"
                    style={{ display: "flex", gap: 16, marginTop: 8 }}
                    onChange={(event) => {
                      handleValueChange();
                      if (event.target.value === "instagram") {
                        onPlatformInstagramSelected?.();
                      }
                      if (event.target.value === "tiktok") {
                        onPlatformTiktokSelected?.();
                      }
                    }}
                    disabled={loading}
                    options={[
                      {
                        label: (
                          <>
                            <InstagramOutlined /> Instagram
                          </>
                        ),
                        value: "instagram",
                      },
                      {
                        label: (
                          <>
                            <TikTokOutlined /> TikTok
                          </>
                        ),
                        value: "tiktok",
                      },
                    ]}
                  />
                </Form.Item>
              </Collapse.Panel>
            </Collapse>
          )}

          <Collapse
            defaultActiveKey={[]}
            className="campaign-offer-tiers"
            bordered={false}
          >
            <Collapse.Panel
              header={
                <div
                  style={{
                    fontWeight: 400,
                    fontSize: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FontAwesomeIcon icon={faFilter} color="#ff9f1c" /> Advanced
                  Filters
                </div>
              }
              key="filters"
            >
              <Form.Item
                name="creatorCategories"
                label={
                  <span>
                    <FontAwesomeIcon icon={faTags} color="#ff9f1c" /> Category
                  </span>
                }
              >
                <TreeSelectWithTags
                  loading={loadingCategories}
                  treeData={categoryOptions}
                  treeCheckable
                  showCheckedStrategy={TreeSelect.SHOW_PARENT}
                  placeholder="Search for categories..."
                  onChange={handleValueChange}
                />
              </Form.Item>

              <Form.Item
                style={{ marginTop: 10 }}
                name="gender"
                label={
                  <span>
                    <FontAwesomeIcon icon={faVenusMars} color="#ff9f1c" />{" "}
                    Gender
                  </span>
                }
              >
                <Select
                  style={{ width: "100%" }}
                  onChange={handleValueChange}
                  disabled={loading}
                  options={[
                    { value: "all", label: "All Genders" },
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                    { value: "nonBinary", label: "Non binary" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                style={{ marginTop: 10 }}
                name="dietaryRestrictions"
                label={
                  <span>
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      color="#ff9f1c"
                    />{" "}
                    Allergies
                  </span>
                }
              >
                <MultiSelectWithTags
                  options={dietaryRestrictions}
                  onChange={handleValueChange}
                  className="campaign-venues"
                  placeholder="Search for dietary restrictions..."
                  disabled={loading}
                />
              </Form.Item>
              <div style={{ marginBottom: 24 }}>
                <Form.Item
                  name="minRating"
                  label={
                    <span>
                      <FontAwesomeIcon icon={faStar} color="#ff9f1c" /> Rating
                    </span>
                  }
                >
                  <InputNumber
                    min={0}
                    max={100}
                    style={{ width: "100%" }}
                    onBlur={handleValueChange}
                    disabled={loading}
                  />
                </Form.Item>
              </div>
            </Collapse.Panel>
          </Collapse>
        </Col>
        {locationAvailability?.foodCompInfluencersTotal === 0 &&
        locationAvailability.mediumCashCompInfluencersTotal === 0 &&
        locationAvailability.largeCashCompInfluencersTotal === 0 ? (
          <Col className="campaign-offer-right">
            <Spin spinning={loading} tip="Looking for creators around...">
              <div
                className="no-influencers-found"
                style={{
                  borderRadius: 16,
                  padding: "48px 32px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    background: "#ff9f1c",
                    borderRadius: "50%",
                    width: 80,
                    height: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 32,
                  }}
                >
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ color: "#fff", fontSize: 32 }}
                  />
                </div>
                <Typography.Title
                  level={3}
                  style={{
                    fontWeight: 800,
                    marginBottom: 8,
                    textAlign: "center",
                  }}
                >
                  No Creators Found <br /> At Those Locations
                </Typography.Title>
                <Typography.Text
                  style={{
                    color: "#666",
                    fontSize: 19,
                    textAlign: "center",
                    marginTop: 16,
                    marginBottom: 32,
                    display: "block",
                    maxWidth: 420,
                  }}
                >
                  Your current criteria is too strict. No creators match your
                  requirements for ${formatNumberKMBT(minFollowersAmount)}+
                  followers with {formatUSD(foodComp)} product compensation
                  value.
                </Typography.Text>
              </div>
            </Spin>
          </Col>
        ) : (
          <Col
            className="campaign-offer-right"
            style={{ padding: 20, background: "#fff" }}
          >
            <div>
              <div
                style={{
                  fontWeight: 400,
                  fontSize: 20,
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <FontAwesomeIcon icon={faUsers} color="#ff9f1c" /> Matched
                Available Creators
              </div>
              <Spin spinning={loading} tip="Looking for creators around...">
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => {
                    const tiers = getFieldValue("tiers") || [];

                    return (
                      <>
                        {locationAvailability?.tiers?.map(
                          (tier: any, index: number) => (
                            <InfluencerCompBox
                              key={tier.id}
                              title={
                                <>
                                  <FontAwesomeIcon
                                    icon={
                                      index === 0
                                        ? faUtensils
                                        : index === 1
                                        ? faMoneyBill
                                        : faMoneyBills
                                    }
                                    style={{
                                      color:
                                        index === 0
                                          ? "#06C167"
                                          : index === 1
                                          ? "#ff9f1c"
                                          : index === 2
                                          ? "#1F3FFF"
                                          : "#000",
                                    }}
                                  />{" "}
                                  {tier.name}
                                </>
                              }
                              color={
                                index === 0
                                  ? "#06C167"
                                  : index === 1
                                  ? "#ff9f1c"
                                  : index === 2
                                  ? "#1F3FFF"
                                  : "#000"
                              }
                              borderColor={
                                index === 0
                                  ? "#06C167"
                                  : index === 1
                                  ? "#ff9f1c"
                                  : index === 2
                                  ? "#1F3FFF"
                                  : "#000"
                              }
                              disabled={
                                !getTierFromID(tier?._id, tiers)?.enabled
                              }
                              available={tier.amountCreators || 0}
                              influencers={tier.creators || []}
                              range={
                                tier.id === "medium"
                                  ? "$30-$100 range"
                                  : tier.id === "large"
                                  ? "$100-$200 range"
                                  : undefined
                              }
                              extraTag={
                                tier.id === "food" ? (
                                  <div
                                    style={{
                                      background: "#06C167",
                                      color: "#fff",
                                      borderRadius: 16,
                                      padding: "2px 16px",
                                      fontWeight: 700,
                                      fontSize: 15,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <CrownFilled />{" "}
                                    {subscription?.planName
                                      ? `Included in ${subscription.planName}`
                                      : `Included in your plan`}
                                  </div>
                                ) : undefined
                              }
                            />
                          )
                        )}
                      </>
                    );
                  }}
                </Form.Item>
                {/* Custom Tiers
                <InfluencerCompBox
                  title={
                    <>
                      <FontAwesomeIcon
                        icon={faUtensils}
                        style={{ color: "#06C167" }}
                      />{" "}
                      Prod Comp Only
                    </>
                  }
                  color="#06C167"
                  borderColor="#06C167"
                  disabled={!tierFoodComp}
                  available={
                    locationAvailability?.foodCompInfluencersTotal || 0
                  }
                  influencers={locationAvailability?.foodCompInfluencers || []}
                  extraTag={
                    <div
                      style={{
                        background: "#06C167",
                        color: "#fff",
                        borderRadius: 16,
                        padding: "2px 16px",
                        fontWeight: 700,
                        fontSize: 15,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <CrownFilled />{" "}
                      {subscription?.planName
                        ? `Included in ${subscription.planName}`
                        : `Included in your plan`}
                    </div>
                  }
                />
               
                <InfluencerCompBox
                  title={
                    <>
                      <FontAwesomeIcon
                        icon={faMoneyBill}
                        style={{ color: "#ff9f1c" }}
                      />{" "}
                      Medium Comp
                    </>
                  }
                  color="#ff9f1c"
                  borderColor="#ff9f1c"
                  disabled={!tierMedium}
                  available={
                    locationAvailability?.mediumCashCompInfluencersTotal || 0
                  }
                  influencers={
                    locationAvailability?.mediumCashCompInfluencers || []
                  }
                  range="$30-$100 range"
                />
                <InfluencerCompBox
                  title={
                    <>
                      <FontAwesomeIcon
                        icon={faMoneyBills}
                        style={{ color: "#1F3FFF" }}
                      />{" "}
                      Large Comp
                    </>
                  }
                  color="#1F3FFF"
                  borderColor="#1F3FFF"
                  disabled={!tierLarge}
                  available={
                    locationAvailability?.largeCashCompInfluencersTotal || 0
                  }
                  influencers={
                    locationAvailability?.largeCashCompInfluencers || []
                  }
                  range="$100-$200 range"
                /> */}
              </Spin>
            </div>
          </Col>
        )}
      </Row>
      <Row style={{ gap: 32, marginTop: 32, justifyContent: "space-between" }}>
        <Button className="text-strong-large" onClick={onClickBack}>
          {textBack}
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={() => {
            onOfferContinueClicked?.();
            onClickNext();
          }}
        >
          {textNext}
        </Button>
      </Row>
    </>
  );
};

export default OfferStep;
